import { z } from 'zod';

import {
  JUMPER_BANDWIDTH_OPTIONS_MHZ,
  MAX_JUMPER_DURATION_MINUTES,
  MAX_JUMPER_SESSION_SPAN_MHZ,
  MAX_JUMPER_SUPPRESSION_MAX_COUNT,
  MAX_JUMPER_SUPPRESSION_TIME_S,
  MIN_JUMPER_DURATION_MINUTES,
  MIN_JUMPER_SUPPRESSION_MAX_COUNT,
  MIN_JUMPER_SUPPRESSION_TIME_S,
} from '../constants/jumper-sessions.js';
import { t } from '../i18n/validation.js';

const requiredFiniteNumber = (message: string) =>
  z.preprocess(
    (value) => {
      if (typeof value !== 'string') return value;

      const trimmed = value.trim();
      return trimmed === '' ? undefined : Number(trimmed);
    },
    z.number({ error: message }).refine(Number.isFinite, message),
  );

const buildFrequencySchema = (
  invalidMessage: string,
  minAllowedMhz: number | null,
  maxAllowedMhz: number | null,
) => {
  let schema: z.ZodType<number, unknown> = requiredFiniteNumber(invalidMessage).refine(
    (value) => value > 0,
    {
      message: invalidMessage,
    },
  );

  if (minAllowedMhz !== null && minAllowedMhz > 0) {
    schema = schema.refine((value) => value >= minAllowedMhz, {
      message: t('validation.frequency.frequencyMin', { min: minAllowedMhz }),
    });
  }

  if (maxAllowedMhz !== null && maxAllowedMhz > 0) {
    schema = schema.refine((value) => value <= maxAllowedMhz, {
      message: t('validation.frequency.frequencyMax', { max: maxAllowedMhz }),
    });
  }

  return schema;
};

export const buildJumperSessionSchema = (
  minAllowedMhz: number | null,
  maxAllowedMhz: number | null,
) => {
  const startSchema = buildFrequencySchema(
    t('validation.frequency.startInvalid'),
    minAllowedMhz,
    maxAllowedMhz,
  );
  const endSchema = buildFrequencySchema(
    t('validation.frequency.endInvalid'),
    minAllowedMhz,
    maxAllowedMhz,
  );
  const durationSchema = requiredFiniteNumber(t('validation.jumper.durationInvalid'))
    .refine((value) => Number.isInteger(value), {
      message: t('validation.jumper.durationInvalid'),
    })
    .refine((value) => value >= MIN_JUMPER_DURATION_MINUTES, {
      message: t('validation.jumper.durationMin', { min: MIN_JUMPER_DURATION_MINUTES }),
    })
    .refine((value) => value <= MAX_JUMPER_DURATION_MINUTES, {
      message: t('validation.jumper.durationMax', { max: MAX_JUMPER_DURATION_MINUTES }),
    });

  const suppressionTimeSchema = requiredFiniteNumber(t('validation.jumper.suppressionTimeInvalid'))
    .refine((value) => Number.isInteger(value), {
      message: t('validation.jumper.suppressionTimeInvalid'),
    })
    .refine((value) => value >= MIN_JUMPER_SUPPRESSION_TIME_S, {
      message: t('validation.jumper.suppressionTimeMin', { min: MIN_JUMPER_SUPPRESSION_TIME_S }),
    })
    .refine((value) => value <= MAX_JUMPER_SUPPRESSION_TIME_S, {
      message: t('validation.jumper.suppressionTimeMax', { max: MAX_JUMPER_SUPPRESSION_TIME_S }),
    });

  const suppressionMaxCountSchema = requiredFiniteNumber(
    t('validation.jumper.suppressionMaxCountInvalid'),
  )
    .refine((value) => Number.isInteger(value), {
      message: t('validation.jumper.suppressionMaxCountInvalid'),
    })
    .refine((value) => value >= MIN_JUMPER_SUPPRESSION_MAX_COUNT, {
      message: t('validation.jumper.suppressionMaxCountMin', {
        min: MIN_JUMPER_SUPPRESSION_MAX_COUNT,
      }),
    })
    .refine((value) => value <= MAX_JUMPER_SUPPRESSION_MAX_COUNT, {
      message: t('validation.jumper.suppressionMaxCountMax', {
        max: MAX_JUMPER_SUPPRESSION_MAX_COUNT,
      }),
    });

  const bandwidthSchema = requiredFiniteNumber(t('validation.jumper.bandwidthInvalid')).refine(
    (value) => JUMPER_BANDWIDTH_OPTIONS_MHZ.includes(value),
    { message: t('validation.jumper.bandwidthInvalid') },
  );

  return z
    .object({
      startFrequency: startSchema,
      endFrequency: endSchema,
      duration: durationSchema,
      suppressionTime: suppressionTimeSchema,
      suppressionMaxCount: suppressionMaxCountSchema,
      bandwidth: bandwidthSchema,
    })
    .refine((values) => values.endFrequency > values.startFrequency, {
      path: ['endFrequency'],
      message: t('validation.frequency.endGreaterThanStart'),
    })
    .refine(
      (values) => values.endFrequency - values.startFrequency <= MAX_JUMPER_SESSION_SPAN_MHZ,
      {
        path: ['endFrequency'],
        message: t('validation.jumper.spanMax', { max: MAX_JUMPER_SESSION_SPAN_MHZ }),
      },
    );
};

export type JumperSessionFormValues = z.infer<ReturnType<typeof buildJumperSessionSchema>>;
